import { useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAudioPreference } from "./useAudioPreference";

export function useAudioNavigation({
  to,
  fallbackMs = 8000,
  safetyNetMs = 15000,
  beforeNavigate,
}) {
  const navigate = useNavigate();
  const [playing] = useAudioPreference();
  const navigated = useRef(false);
  const safetyTimer = useRef(null);
  const fallbackTimer = useRef(null);

  const go = useCallback(() => {
    if (navigated.current) {
      return;
    }

    navigated.current = true;
    clearTimeout(safetyTimer.current);
    clearTimeout(fallbackTimer.current);
    beforeNavigate?.();
    navigate(to);
  }, [beforeNavigate, navigate, to]);

  useEffect(() => {
    if (!playing) {
      // Audio is muted / disabled - use a short fallback so the page isn't stuck
      fallbackTimer.current = setTimeout(go, fallbackMs);
    } else {
      // Audio is on - set a safety net in case onEnded never fires
      safetyTimer.current = setTimeout(go, safetyNetMs);
    }

    return () => {
      clearTimeout(safetyTimer.current);
      clearTimeout(fallbackTimer.current);
    };
  }, [playing, go, fallbackMs, safetyNetMs]);

  return {
    onEnded: go, // primary trigger when audio is playing
    onError: go,
  };
}
